import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import api from '@/utils/api';
import { toast } from 'sonner';
import { LogOut, Search, Shield, User, Star, StarOff, GitCompare, X, Bookmark } from 'lucide-react';
import MessagesNavButton from '@/components/MessagesNavButton';
import XVAssistWidget from '@/components/XVAssistWidget';
import BillingCard from '@/components/BillingCard';

const SA_PROVINCES = ["Eastern Cape","Western Cape","Gauteng","KwaZulu-Natal","Limpopo","Mpumalanga","North West","Free State","Northern Cape"];

function PlayerCard({ item, isShortlisted, onToggleShortlist, onAddToCompare, compareSelected }) {
  return (
    <Card className="bg-zinc-900 border-zinc-800 hover:border-zinc-700 transition-colors" data-testid={`player-card-${item.profile.id}`}>
      <div className="p-6">
        <div className="flex justify-between items-start mb-4">
          <div>
            <h3 className="text-xl font-heading font-bold mb-1">{item.user.full_name}</h3>
            <p className="text-zinc-400 text-sm">{item.profile.position}</p>
          </div>
          <div className="flex gap-1">
            {item.profile.verified && (
              <Badge className="bg-blue-500/10 text-blue-400 border border-blue-500/20"><Shield className="w-3 h-3 mr-1" />Verified</Badge>
            )}
            {item.testing_data_source === 'coach_validated' && (
              <Badge className="bg-green-500/10 text-green-400 border border-green-500/20" data-testid={`validated-data-badge-${item.profile.id}`}>
                <Shield className="w-3 h-3 mr-1" />Validated Data
              </Badge>
            )}
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3 text-sm">
          <div><p className="text-zinc-500">Gender</p><p className="font-mono font-semibold capitalize">{item.profile.gender}</p></div>
          <div><p className="text-zinc-500">Age</p><p className="font-mono font-semibold">{item.profile.age}</p></div>
          <div><p className="text-zinc-500">Height</p><p className="font-mono font-semibold">{item.profile.height} cm</p></div>
          <div><p className="text-zinc-500">Weight</p><p className="font-mono font-semibold">{item.profile.weight} kg</p></div>
        </div>
        <div className="mt-4 pt-4 border-t border-zinc-800">
          <p className="text-zinc-500 text-xs mb-1">School/Club</p>
          <p className="text-sm font-semibold">{item.profile.school_club}</p>
        </div>
        <div className="mt-3">
          <p className="text-zinc-500 text-xs mb-1">Location</p>
          <p className="text-sm">{item.profile.town}, {item.profile.province}</p>
        </div>
        <div className="flex gap-2 mt-4 pt-4 border-t border-zinc-800">
          <Button size="sm" variant={isShortlisted ? "default" : "outline"} onClick={() => onToggleShortlist(item.profile.id)}
            className={isShortlisted ? "bg-yellow-500 text-black hover:bg-yellow-600 h-8 flex-1" : "border-zinc-700 text-zinc-400 hover:text-white h-8 flex-1"}
            data-testid={`shortlist-btn-${item.profile.id}`}>
            {isShortlisted ? <><StarOff className="w-3 h-3 mr-1" />Remove</> : <><Star className="w-3 h-3 mr-1" />Shortlist</>}
          </Button>
          <Button size="sm" variant="outline" onClick={() => onAddToCompare(item.profile.id)}
            className={`border-zinc-700 h-8 flex-1 ${compareSelected.includes(item.profile.id) ? 'border-primary-neon text-primary-neon' : 'text-zinc-400 hover:text-white'}`}
            data-testid={`compare-btn-${item.profile.id}`}>
            <GitCompare className="w-3 h-3 mr-1" />Compare
          </Button>
        </div>
      </div>
    </Card>
  );
}

export default function ScoutDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [players, setPlayers] = useState([]);
  const [shortlist, setShortlist] = useState([]);
  const [shortlistedIds, setShortlistedIds] = useState(new Set());
  const [loading, setLoading] = useState(false);
  const [compareSelected, setCompareSelected] = useState([]);
  const [compareData, setCompareData] = useState(null);
  const [showCompare, setShowCompare] = useState(false);

  const [filters, setFilters] = useState({
    position: '', min_age: '', max_age: '', province: '', gender: '', verified_only: false, validated_only: false
  });

  const fetchShortlist = useCallback(async () => {
    try {
      const res = await api.get('/scout/shortlist');
      setShortlist(res.data);
      setShortlistedIds(new Set(res.data.map(s => s.shortlist_entry.player_id)));
    } catch (e) { console.error('Failed to load shortlist', e); }
  }, []);

  useEffect(() => { handleSearch(); fetchShortlist(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSearch = async () => {
    setLoading(true);
    try {
      const searchData = {
        position: filters.position || undefined,
        min_age: filters.min_age ? parseInt(filters.min_age) : undefined,
        max_age: filters.max_age ? parseInt(filters.max_age) : undefined,
        province: filters.province || undefined,
        gender: filters.gender || undefined,
        verified_only: filters.verified_only,
        validated_only: filters.validated_only
      };
      const res = await api.post('/players/search', searchData);
      setPlayers(res.data);
    } catch { toast.error('Failed to search players'); }
    finally { setLoading(false); }
  };

  const handleToggleShortlist = async (playerId) => {
    if (shortlistedIds.has(playerId)) {
      try {
        await api.delete(`/scout/shortlist/${playerId}`);
        toast.success('Removed from shortlist');
        fetchShortlist();
      } catch { toast.error('Failed to remove'); }
    } else {
      try {
        await api.post(`/scout/shortlist/${playerId}`);
        toast.success('Added to shortlist');
        fetchShortlist();
      } catch (e) { toast.error(e.response?.data?.detail || 'Failed to add'); }
    }
  };

  const handleAddToCompare = (playerId) => {
    setCompareSelected(prev => {
      if (prev.includes(playerId)) return prev.filter(id => id !== playerId);
      if (prev.length >= 4) { toast.error('Max 4 players for comparison'); return prev; }
      return [...prev, playerId];
    });
  };

  const handleCompare = async () => {
    if (compareSelected.length < 2) { toast.error('Select at least 2 players to compare'); return; }
    try {
      const res = await api.post('/scout/compare', compareSelected);
      setCompareData(res.data);
      setShowCompare(true);
    } catch { toast.error('Failed to compare players'); }
  };

  return (
    <div className="min-h-screen bg-zinc-950 text-white" data-testid="scout-dashboard">
      <nav className="border-b border-zinc-800 bg-zinc-900/50" data-testid="scout-nav">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center gap-3">
              <img src="/logo.png" alt="FutureXV" className="h-10" />
              <span className="text-zinc-400">|</span>
              <span className="font-heading font-bold">Scout Dashboard</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-zinc-400 text-sm hidden sm:block">{user?.email}</span>
              <MessagesNavButton />
              <Button onClick={() => { logout(); navigate('/'); }} variant="ghost" className="text-zinc-400 hover:text-white" data-testid="logout-button">
                <LogOut className="w-4 h-4 mr-2" />Logout
              </Button>
            </div>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-4xl font-heading font-bold mb-2" data-testid="scout-welcome">Welcome, {user.full_name}</h1>
          <p className="text-zinc-400 font-body">Discover, shortlist and compare talented rugby players</p>
        </div>

        <BillingCard />

        <Tabs defaultValue="search" className="w-full">
          <TabsList className="grid w-full grid-cols-3 mb-8 bg-zinc-900 max-w-lg" data-testid="scout-tabs">
            <TabsTrigger value="search" className="data-[state=active]:bg-primary-neon data-[state=active]:text-black"><Search className="w-4 h-4 mr-2" />Search</TabsTrigger>
            <TabsTrigger value="shortlist" className="data-[state=active]:bg-primary-neon data-[state=active]:text-black"><Bookmark className="w-4 h-4 mr-2" />Shortlist ({shortlist.length})</TabsTrigger>
            <TabsTrigger value="compare" className="data-[state=active]:bg-primary-neon data-[state=active]:text-black"><GitCompare className="w-4 h-4 mr-2" />Compare</TabsTrigger>
          </TabsList>

          <TabsContent value="search">
            <Card className="bg-zinc-900 border-zinc-800 p-6 mb-6" data-testid="search-filters-card">
              <h2 className="text-xl font-heading font-bold mb-6 flex items-center"><Search className="w-5 h-5 mr-2 text-primary-neon" />Search Filters</h2>
              <div className="grid md:grid-cols-3 lg:grid-cols-6 gap-4 mb-4">
                <div>
                  <Label className="text-zinc-300 text-sm">Gender</Label>
                  <Select value={filters.gender || "all"} onValueChange={(v) => setFilters({...filters, gender: v === "all" ? "" : v})}>
                    <SelectTrigger data-testid="filter-gender" className="bg-zinc-950 border-zinc-800 text-white mt-1 h-10"><SelectValue placeholder="Any" /></SelectTrigger>
                    <SelectContent className="bg-zinc-900 border-zinc-800 text-white">
                      <SelectItem value="all">Any</SelectItem><SelectItem value="male">Male</SelectItem><SelectItem value="female">Female</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div><Label className="text-zinc-300 text-sm">Position</Label><Input data-testid="filter-position" value={filters.position} onChange={(e) => setFilters({...filters, position: e.target.value})} placeholder="Any" className="bg-zinc-950 border-zinc-800 text-white mt-1 h-10" /></div>
                <div><Label className="text-zinc-300 text-sm">Min Age</Label><Input data-testid="filter-min-age" type="number" value={filters.min_age} onChange={(e) => setFilters({...filters, min_age: e.target.value})} placeholder="Any" className="bg-zinc-950 border-zinc-800 text-white mt-1 h-10" /></div>
                <div><Label className="text-zinc-300 text-sm">Max Age</Label><Input data-testid="filter-max-age" type="number" value={filters.max_age} onChange={(e) => setFilters({...filters, max_age: e.target.value})} placeholder="Any" className="bg-zinc-950 border-zinc-800 text-white mt-1 h-10" /></div>
                <div>
                  <Label className="text-zinc-300 text-sm">Province</Label>
                  <Select value={filters.province || "all"} onValueChange={(v) => setFilters({...filters, province: v === "all" ? "" : v})}>
                    <SelectTrigger data-testid="filter-province" className="bg-zinc-950 border-zinc-800 text-white mt-1 h-10"><SelectValue placeholder="Any" /></SelectTrigger>
                    <SelectContent className="bg-zinc-900 border-zinc-800 text-white">
                      <SelectItem value="all">Any Province</SelectItem>
                      {SA_PROVINCES.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-end">
                  <div className="flex items-center space-x-2">
                    <Switch data-testid="filter-verified" checked={filters.verified_only} onCheckedChange={(checked) => setFilters({...filters, verified_only: checked})} className="data-[state=checked]:bg-primary-neon" />
                    <Label className="text-zinc-300 text-sm">Verified Only</Label>
                  </div>
                </div>
                <div className="flex items-end">
                  <div className="flex items-center space-x-2">
                    <Switch data-testid="filter-validated" checked={filters.validated_only} onCheckedChange={(checked) => setFilters({...filters, validated_only: checked})} className="data-[state=checked]:bg-green-500" />
                    <Label className="text-zinc-300 text-sm">Validated Data Only</Label>
                  </div>
                </div>
              </div>
              <div className="flex gap-3">
                <Button onClick={handleSearch} className="btn-primary px-8" disabled={loading} data-testid="search-button">{loading ? 'Searching...' : 'Search Players'}</Button>
                {compareSelected.length >= 2 && (
                  <Button onClick={handleCompare} variant="outline" className="border-primary-neon text-primary-neon hover:bg-primary-neon/10" data-testid="compare-selected-button">
                    <GitCompare className="w-4 h-4 mr-2" />Compare {compareSelected.length} Selected
                  </Button>
                )}
              </div>
            </Card>

            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-heading font-bold" data-testid="results-heading">Search Results ({players.length})</h2>
            </div>

            {loading ? (
              <div className="text-center py-12 text-zinc-500">Loading...</div>
            ) : players.length === 0 ? (
              <div className="text-center py-12 text-zinc-500" data-testid="no-results-message"><User className="w-12 h-12 mx-auto mb-4 opacity-50" /><p>No players found. Try adjusting your filters.</p></div>
            ) : (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {players.map((item) => (
                  <PlayerCard key={item.profile.id} item={item} isShortlisted={shortlistedIds.has(item.profile.id)}
                    onToggleShortlist={handleToggleShortlist} onAddToCompare={handleAddToCompare} compareSelected={compareSelected} />
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="shortlist">
            <h2 className="text-2xl font-heading font-bold mb-6" data-testid="shortlist-heading">Your Shortlist ({shortlist.length})</h2>
            {shortlist.length === 0 ? (
              <Card className="bg-zinc-900 border-zinc-800 p-12 text-center" data-testid="empty-shortlist">
                <Star className="w-12 h-12 mx-auto mb-4 text-zinc-700" />
                <p className="text-zinc-500">Your shortlist is empty. Add players from the search results.</p>
              </Card>
            ) : (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {shortlist.map((item) => (
                  <PlayerCard key={item.shortlist_entry.player_id}
                    item={{ profile: item.profile, user: item.user }}
                    isShortlisted={true}
                    onToggleShortlist={handleToggleShortlist}
                    onAddToCompare={handleAddToCompare}
                    compareSelected={compareSelected} />
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="compare">
            <div className="mb-6">
              <h2 className="text-2xl font-heading font-bold mb-2" data-testid="compare-heading">Player Comparison</h2>
              <p className="text-zinc-400 text-sm">Select 2-4 players from search results, then click "Compare Selected" to see a side-by-side analysis.</p>
            </div>

            {showCompare && compareData && compareData.length >= 2 ? (
              <div>
                <div className="flex justify-between items-center mb-4">
                  <Badge className="bg-primary-neon/10 text-primary-neon border border-primary-neon/20">Comparing {compareData.length} players</Badge>
                  <Button size="sm" variant="ghost" onClick={() => { setShowCompare(false); setCompareSelected([]); }} className="text-zinc-400" data-testid="clear-compare-button"><X className="w-4 h-4 mr-1" />Clear</Button>
                </div>
                <Card className="bg-zinc-900 border-zinc-800 overflow-hidden" data-testid="compare-table">
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-zinc-950 border-b border-zinc-800">
                        <tr>
                          <th className="px-5 py-4 text-left text-xs font-heading font-bold text-zinc-400 uppercase">Attribute</th>
                          {compareData.map((p, i) => (
                            <th key={p.user?.id || i} className="px-5 py-4 text-left text-xs font-heading font-bold text-primary-neon uppercase">{p.user?.full_name}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {[
                          { label: 'Position', key: 'position' },
                          { label: 'Age', key: 'age' },
                          { label: 'Gender', key: 'gender', capitalize: true },
                          { label: 'Height (cm)', key: 'height' },
                          { label: 'Weight (kg)', key: 'weight' },
                          { label: 'School/Club', key: 'school_club' },
                          { label: 'Province', key: 'province' },
                          { label: 'Town', key: 'town' },
                          { label: 'Verified', key: 'verified', bool: true },
                          { label: 'Videos', key: 'video_count', fromRoot: true }
                        ].map((attr) => (
                          <tr key={attr.label} className="border-b border-zinc-800 hover:bg-zinc-800/30">
                            <td className="px-5 py-3 text-sm text-zinc-400 font-semibold">{attr.label}</td>
                            {compareData.map((p, i) => {
                              const val = attr.fromRoot ? p[attr.key] : p.profile?.[attr.key];
                              return (
                                <td key={p.user?.id || i} className={`px-5 py-3 text-sm text-white ${attr.capitalize ? 'capitalize' : ''}`}>
                                  {attr.bool ? (val ? <Badge className="bg-green-500/10 text-green-400 border border-green-500/20">Yes</Badge> : <Badge className="bg-zinc-500/10 text-zinc-400 border border-zinc-500/20">No</Badge>) : (val ?? '-')}
                                </td>
                              );
                            })}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </Card>
              </div>
            ) : (
              <Card className="bg-zinc-900 border-zinc-800 p-12 text-center" data-testid="compare-empty">
                <GitCompare className="w-12 h-12 mx-auto mb-4 text-zinc-700" />
                <p className="text-zinc-500 mb-4">No comparison active. Select players from search results using the "Compare" button, then click "Compare Selected".</p>
                {compareSelected.length > 0 && (
                  <Button onClick={handleCompare} className="btn-primary" data-testid="run-compare-button">
                    <GitCompare className="w-4 h-4 mr-2" />Compare {compareSelected.length} Selected Players
                  </Button>
                )}
              </Card>
            )}
          </TabsContent>
        </Tabs>
      </div>
      <XVAssistWidget />
    </div>
  );
}
